import React, { useCallback, useMemo, useRef, useLayoutEffect } from "react";
import styled, { useTheme } from "styled-components";
import { TextLoader, getText, colors2, UI_BODY_SM_DF, UI_CAPTION_DF, CheckboxButton, icons, TooltipV2 } from "oolib";
import { genIcon } from "../utils/genIcon";
import { genColor } from "../utils/genColor";
import { StyledListItemWrapper, StyledWrapper, StyledRightSection, StyledCheckboxArea, StyledSmallCheckbox } from "./styled";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useLightbox } from "@/components/UI/lightbox";

const { CaretRight } = icons;
const { grey10, grey40, grey80, grey60 } = colors2

export const ListItem = React.memo(({
  id,
  option,
  isSelected,
  isFocussed,
  scrollFocussedOpIntoView,
  optionsClassName,
  S,
  observerRef,
  isTagsStyle,
  isMulti,
  value,
  index,
  alignCheckbox = "left",
  iconAfter,
  style,
  onDragStart,
  onDragOver,
  groupIndex = null, // Group index for group-confined drag selection
  optimisticSelections, // Set of option values being toggled during drag
  onChange, // Memoized handler from parent
  hasSubmenu = false, // Opt-in: row owns a cascade panel (chevron + active bg)
  // When hasSubmenu, whether to draw the caret glyph. Turned off for a row whose
  // drilldown is disabled (e.g. a selected value in exclusive mode) so the caret —
  // dead UI at that point — is hidden WITHOUT changing the rest of the submenu row
  // layout, i.e. no padding/checkbox-area shift vs its siblings.
  showSubmenuCaret = true
}) => {
  const theme = useTheme();
  // const TooltipV2 = theme?.TooltipV2;
  const lightbox = useLightbox(); // null when ListItem is used outside a Lightbox
  const { _Theme } = useGetQueryData("platformConfigs");
  const secondaryContainerColor = _Theme?.colors?.secondaryContainer || colors2.secondaryContainer;
  const TypoComp = S ? UI_BODY_SM_DF : UI_BODY_SM_DF;
  // `displayNode` (optional): a ready-made React node rendered in place of the
  // plain `display` text. Lets a row show rich content (e.g. a read-only
  // editor) that getText() would otherwise flatten to a string.
  // `onHoverChange(isHovered)` (optional): fired on row mouse enter/leave. Lets
  // a consumer react to which row is hovered (e.g. highlight a linked element).
  // `disabledReason` (optional, see @/utils/disabledReasons): disables the row and
  // shows the reason as its tooltip.
  const { image, display, displayNode, icon, iconWeight, desc, color, loading, disabledReason, onHoverChange } = option;
  const disabled = option.disabled || !!disabledReason;
  const tooltip = option.tooltip ?? disabledReason;

  // Check if option is selected (with XOR logic for optimistic selections during drag)
  const opIsSelected = useCallback((thisOpData) => {
    const actuallySelected = Array.isArray(value) && value.some((d) => d.value === thisOpData.value);
    const inOptimistic = optimisticSelections?.has(thisOpData.value);

    // If not in optimistic set, return actual state
    if (!inOptimistic) return actuallySelected;

    // XOR: if in optimistic set, flip the actual state for visual feedback
    return !actuallySelected;
  }, [value, optimisticSelections]);

  // Memoized check for current option's selected state
  const isOptionSelected = useMemo(() => opIsSelected(option), [opIsSelected, option]);

  // Handle mouse down for drag-select (multi-select only)
  const handleMouseDown = useCallback((e) => {
    if (isMulti && !isTagsStyle && !disabled && !loading && onDragStart) {
      e.preventDefault(); // Prevent text selection during drag
      onDragStart(index, option, isOptionSelected, groupIndex);
    }
  }, [isMulti, isTagsStyle, disabled, loading, onDragStart, index, option, isOptionSelected, groupIndex]);

  // Handle mouse enter for drag-select (multi-select only)
  const handleMouseEnter = useCallback(() => {
    if (isMulti && !isTagsStyle && !disabled && !loading && onDragOver) {
      onDragOver(index, option, groupIndex);
    }
  }, [isMulti, isTagsStyle, disabled, loading, onDragOver, index, option, groupIndex]);

  // For multi-select, we handle clicks via drag system (mousedown+mouseup)
  // For single-select, we use regular onClick via onChange.
  // Forward the event so consumers can stopPropagation / read modifiers.
  const handleClickWrapper = useCallback((e) => {
    if (!isMulti || isTagsStyle) {
      onChange?.(option, e);
    }
    // For multi-select without tagsStyle, clicks are handled by drag system
  }, [isMulti, isTagsStyle, onChange, option]);

  const renderCheckBox = useCallback(() => {
    if (isMulti && !isTagsStyle) {
      return <StyledSmallCheckbox isSelected={isOptionSelected} className="radioOrCheckboxButton_inside_input" />
    }
    return null;
  }, [isMulti, isTagsStyle, isOptionSelected]);

  if (loading) {
    return (
      <StyledListItemWrapper
        isSelected={isSelected}
        isFocussed={isFocussed}
        className="OKE-Dropdown__option"
        S={S}
        secondaryContainerColor={secondaryContainerColor}
      >
        <div style={{ display: "flex", gap: "1rem" }}>
        {isMulti && <TextLoader style={{ width: "2rem" }}/> }
        <TextLoader style={{ width: "8rem", height: "1.5rem" }} />
        </div>
      </StyledListItemWrapper>
    );
  }

  // Split-checkbox + reference-override only make sense for rows that ARE the
  // trigger of a sub-Lightbox (folders in inline-multilayer mode). For leaves,
  // overriding the ambient Lightbox's reference to the leaf itself moves the
  // floating panel onto one of its own children — see PR #1684 regression on
  // V2 AnnoLightbox sub-panels.
  const hasLightboxWithMulti = hasSubmenu && isMulti && !isTagsStyle && !!lightbox;
  const coreContentRef = useRef(null);

  // Override lightbox reference to exclude checkbox area
  useLayoutEffect(() => {
    if (hasLightboxWithMulti && coreContentRef.current && lightbox?.refs?.setReference) {
      lightbox.refs.setReference(coreContentRef.current);
    }
  }, [hasLightboxWithMulti, lightbox]);

  const coreContent = (
    <StyledListItemWrapper
      index={index}
      isSelected={hasLightboxWithMulti ? false : isSelected}
      isFocussed={isFocussed}
      className="OKE-Dropdown__option"
      data-lightbox-item
      data-active={hasSubmenu && lightbox?.open || undefined}
      ref={(el) => {
        if (hasLightboxWithMulti) coreContentRef.current = el;
        if (isFocussed && el) scrollFocussedOpIntoView(el);
      }}
      onClick={handleClickWrapper}
      onMouseDown={hasLightboxWithMulti ? undefined : handleMouseDown}
      onMouseEnter={(e) => {
        if (!hasLightboxWithMulti) handleMouseEnter(e);
        onHoverChange?.(true);
      }}
      onMouseLeave={() => onHoverChange?.(false)}
      S={S}
      secondaryContainerColor={secondaryContainerColor}
      style={{
        pointerEvents: disabled && "none",
        cursor: !disabled && "pointer",
        userSelect: "none",
        paddingRight: hasLightboxWithMulti && "0.8rem",
        ...(hasSubmenu && lightbox?.open && { backgroundColor: secondaryContainerColor }),
        ...style
      }}
    >
      <StyledWrapper
        ref={observerRef}
        style={{
          display: "flex",
          alignItems: desc ? "flex-start" : "center",
          gap: icon ? "0.6rem" : "0.8rem",
          flexGrow: 1,
        }}
      >

        {(image || icon || color || (isMulti && !hasLightboxWithMulti)) && (
          <div
            style={{
              marginTop: desc ? "0.1rem" : 0,
              display: "flex",
              alignItems: "center",
              alignSelf: "flex-start",
              gap: "0.6rem",
              flex: "none"
            }}
          >
            {!hasLightboxWithMulti && alignCheckbox === "left" && renderCheckBox()}

            {/* {image && genProfileImg({ image, display, theme })} */}
            {icon &&
              genIcon({
                icon,
                weight: iconWeight,
                S,
                className: "OKE-Dropdown__optionIcon",
                disabled
              })}
            {color && genColor({ color })}
          </div>
        )}
        <div>
          {displayNode ? (
            displayNode
          ) : (
            <TypoComp
              title={getText(display)}
              semibold={desc ? true : false}
              className={`OKE-Dropdown__optionText ${optionsClassName || ""}`}
              style={{ color: disabled ? grey40 : grey80 }}
            >
              {getText(display)}
            </TypoComp>
          )}

          {desc && (
            <UI_CAPTION_DF className="OKE-Dropdown__optionDesc" style={{ color: disabled ? grey40 : grey60, marginTop: "0.2rem" }}>
              {getText(desc)}
            </UI_CAPTION_DF>
          )}
        </div>
      </StyledWrapper>
      <StyledRightSection>
        {!hasLightboxWithMulti && alignCheckbox === "right" && renderCheckBox()}
        {iconAfter && genIcon({
          icon: iconAfter,
          S,
          className: "OKE-Dropdown__optionIcon",
          disabled
        })}
        {hasSubmenu && showSubmenuCaret && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CaretRight size={16} color={grey40} />
          </div>
        )}
      </StyledRightSection>
    </StyledListItemWrapper>
  );

  // `option.tooltip` shows on hover. TooltipV2's own wrapper takes the hover, so it
  // still works on disabled rows, whose own pointer events are off.
  const withTooltip = (node) =>
    tooltip ? (
      <TooltipV2 text={tooltip} placement="right" wrapperDisplay="block" wrapperStyle={{ width: "100%" }}>
        {node}
      </TooltipV2>
    ) : node;

  if (hasLightboxWithMulti) {
    return withTooltip(
      <div data-lightbox-item data-active={lightbox?.open || undefined} style={{ display: "flex", alignItems: "stretch", width: "100%" }}>
        <StyledCheckboxArea
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            // For multi-select with drag wired up, the drag system's mouseup
            // handles the click-as-toggle. Calling onChange here would double-toggle.
            if (!isMulti || isTagsStyle || !onDragStart) onChange?.(option, e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              if (!disabled) onChange?.(option, e);
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown(e);
          }}
          onMouseEnter={handleMouseEnter}
          style={disabled ? { cursor: "default" } : undefined}
        >
          {renderCheckBox()}
        </StyledCheckboxArea>
        {coreContent}
      </div>
    );
  }

  return withTooltip(coreContent);
});
